import React, { useState } from "react";
import { usePageView } from "../../utils/track";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BarChart3, Crosshair, Download, ExternalLink, LayoutGrid, ListOrdered, Target, Trophy } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, Loading, Pagination, RankChip, SectionCard, statusTone } from "../../components/ui";
import CompetitionSidebar from "../../components/CompetitionSidebar";
import ResultsList from "../../components/ResultsList";
import { useApi } from "../../api/client";
import { Aggregates, Competition, CompetitionEvent, EventRankingRow, MatchInfo, ResultRow } from "../../data/types";
import { downloadCsv } from "../../utils/download";
import { usePortalLink } from "../../utils/portalLink";

/*
 * An aggregate is ranked WITHIN a class, never across classes.
 *
 * Target Rifle scores 5 per shot with a V centre, F-Class 6 with an X, and
 * Sporter is a different discipline again — so one combined ladder would place
 * an F-Class shooter above a Target Rifle shooter on the strength of a higher
 * maximum rather than better shooting. Each class gets its own ranked table,
 * headed with the class and its centre notation.
 */
function AggregateTable({ classes }: { classes: { rifleClass: string; rows: ResultRow[] }[] }) {
  const link = usePortalLink();
  const navigate = useNavigate();

  if (!classes.length) {
    return <div className="px-4 py-8 text-center text-[13px] text-muted">No aggregate rows yet — import a shoot to populate them.</div>;
  }

  return (
    <div className="space-y-4">
      {classes.map(({ rifleClass, rows }) => (
        <div key={rifleClass} className="overflow-hidden rounded-xl border border-line">
          <div className="flex items-center justify-between gap-3 border-b border-line bg-slate-50 px-4 py-2">
            <h4 className="text-[13px] font-extrabold text-ink">
              {rifleClass}
              <span className="ml-2 font-semibold text-muted">
                {rows.length} {rows.length === 1 ? "competitor" : "competitors"}
              </span>
            </h4>
            <span className="text-xs font-semibold text-muted">Ranked within {rifleClass}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead>
                <tr className="border-b border-line">
                  <th className="th">Rank</th>
                  <th className="th">Shooter</th>
                  <th className="th">Club</th>
                  <th className="th">Grade</th>
                  <th className="th text-center">Matches</th>
                  <th className="th text-right">Total Score</th>
                  <th className="th text-right">{rows[0]?.centreLabel ? `${rows[0].centreLabel} Count` : "Centres"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((r, i) => (
                  <tr
                    key={`${r.sid}-${r.rifleClass}`}
                    onClick={() => navigate(link(`/shooters/${r.sid}`))}
                    className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
                  >
                    <td className="td"><RankChip rank={r.rank} /></td>
                    <td className="td">
                      <Link to={link(`/shooters/${r.sid}`)} className="font-bold text-ink hover:text-primary">{r.shooter}</Link>
                      <div className="text-xs text-muted">SID: {r.sid}</div>
                    </td>
                    <td className="td text-slate-600">{r.club}</td>
                    <td className="td">{r.grade}</td>
                    <td className="td text-center">{r.matchesCounted}</td>
                    <td className="td text-right font-bold">{r.score}</td>
                    <td className="td text-right font-semibold text-primary">{r.xCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------- Shared shell ------------------------------- */

function CompShell({
  active,
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  active: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: (c: Competition) => React.ReactNode;
}) {
  const link = usePortalLink();
  const { id } = useParams();
  const { data: c, loading } = useApi<(Competition & { eventList?: CompetitionEvent[] }) | null>(
    id ? `/competitions/${id}` : null,
    null
  );

  if (loading) return <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5"><div className="card"><Loading /></div></div>;
  if (!c) {
    return (
      <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
        <div className="card">
          <EmptyState
            title="Event not found"
            message="It may have been removed, or no shoot has been imported yet."
            action={
              <Link to={link("/competitions")} className="text-[13px] font-bold text-primary hover:underline">
                Back to events
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
      <Breadcrumb
        items={[
          { label: "Home", to: "/" },
          { label: "Events", to: "/competitions" },
          { label: c.name, to: `/competitions/${c.id}` },
          { label: title },
        ]}
      />
      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <CompetitionSidebar competition={c} active={active} events={c.eventList} />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-3 text-2xl font-extrabold tracking-tight text-ink">
            <Icon size={28} className="text-primary" /> {title}
          </h1>
          <p className="mt-1 text-[13px] text-muted">{subtitle}</p>
          <div className="mt-6">{children(c)}</div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Matches ---------------------------------- */

export function CompMatches() {
  usePageView("matches");
  const link = usePortalLink();
  const navigate = useNavigate();
  const { data: matches } = useApi<MatchInfo[]>("/matches", []);
  const { data: events } = useApi<CompetitionEvent[]>("/events", []);
  const eventName = (id: string) => {
    const e = events.find((x) => x.id === id);
    return e ? `Event ${e.n}: ${e.name}` : id;
  };
  return (
    <CompShell active="matches" title="Matches" subtitle="Every match in this competition, with live status and results." icon={Crosshair}>
      {(c) => {
        const rows = matches.filter((m) => m.competitionId === c.id);
        return (
          <div className="card overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th">Match</th>
                  <th className="th">Event</th>
                  <th className="th">Date</th>
                  <th className="th">Distance</th>
                  <th className="th text-center">Shots</th>
                  <th className="th text-center">Strings</th>
                  <th className="th">Status</th>
                  <th className="th text-right">Results</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {!rows.length && (
                  <tr>
                    <td colSpan={8} className="td py-8 text-center text-muted">
                      No matches recorded for this competition.
                    </td>
                  </tr>
                )}
                {rows.map((m) => (
                  <tr key={m.id} onClick={() => navigate(link(`/competitions/${c.id}/events/${m.eventId}/matches/${m.id}`))} className={`cursor-pointer transition-colors hover:bg-slate-50 ${m.status === "LIVE" ? "bg-success-soft/40" : ""}`}>
                    <td className="td">
                      <span className="flex items-center gap-3 font-bold"><Target size={17} className="text-primary" /> {m.name}</span>
                    </td>
                    <td className="td text-slate-600">{eventName(m.eventId)}</td>
                    <td className="td">{m.date}</td>
                    <td className="td">{m.distance}</td>
                    <td className="td text-center">{m.shots}</td>
                    <td className="td text-center">{m.strings}</td>
                    <td className="td"><Badge tone={statusTone(m.status)}>{m.status}</Badge></td>
                    <td className="td text-right">
                      <Link
                        to={link(`/competitions/${c.id}/events/${m.eventId}/matches/${m.id}`)}
                        className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-primary px-4 text-[13px] font-bold text-white shadow-sm transition-colors hover:bg-primary-hover"
                      >
                        View Results <ExternalLink size={13} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-line">
              <Pagination summary={`Showing 1 to ${rows.length} of ${rows.length} matches`} pages={1} />
            </div>
          </div>
        );
      }}
    </CompShell>
  );
}

/* -------------------------------- Rankings ---------------------------------- */

/*
 * Who won this event.
 *
 * The sidebar's Rankings row used to open the portal-wide board — every
 * competitor who has ever shot, ranked on their lifetime average. Inside an
 * event that is the wrong question and a confusing answer: the club running the
 * meeting wants to know who won it, and last weekend that took hours of reading
 * match cards to work out.
 *
 * So this is the same event, placed class by class on the aggregate shot here.
 * Nothing qualifies or disqualifies a competitor; if they shot, they are placed.
 */
export function CompRankings() {
  usePageView("event-rankings");
  const link = usePortalLink();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: rows, loading } = useApi<EventRankingRow[]>(id ? `/competitions/${id}/rankings` : null, []);

  // One board per class, in the order the class register gives them.
  const classes = Array.from(new Set(rows.map((r) => r.rifleClass)));

  return (
    <CompShell
      active="rankings"
      title="Event Rankings"
      subtitle="Placings from this event alone, ranked within each class on total score and then centres."
      icon={Trophy}
    >
      {(c) => (
        <div className="space-y-4">
          {loading && <div className="card"><Loading /></div>}
          {!loading && !rows.length && (
            <div className="card">
              <EmptyState
                icon={Trophy}
                title="No placings yet"
                message="They appear as soon as this event's scores are imported."
              />
            </div>
          )}
          {classes.map((cls) => {
            const board = rows.filter((r) => r.rifleClass === cls);
            const winner = board[0];
            return (
              <div key={cls} className="card overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-slate-50 px-4 py-2.5">
                  <h3 className="flex items-center gap-2 text-[15px] font-extrabold text-ink">
                    {cls}
                    <span className="text-[13px] font-semibold text-muted">
                      {board.length} {board.length === 1 ? "competitor" : "competitors"}
                    </span>
                  </h3>
                  {/* The winner named, rather than left to be read off row one. */}
                  {winner && (
                    <span className="flex items-center gap-1.5 text-[13px] font-bold text-success">
                      <Trophy size={14} /> {winner.shooter}
                      <span className="font-semibold text-muted">
                        {winner.score}
                        {winner.centreLabel ? ` · ${winner.xCount}${winner.centreLabel}` : ""}
                      </span>
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px]">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="th">Rank</th>
                        <th className="th">Shooter</th>
                        <th className="th">Club</th>
                        <th className="th">Grade</th>
                        <th className="th text-center">Matches</th>
                        <th className="th text-right">Score</th>
                        <th className="th text-right">{board[0]?.centreLabel ? `${board[0].centreLabel} Count` : "Centres"}</th>
                        <th className="th text-right">Percent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {board.map((r, i) => (
                        <tr
                          key={`${r.sid}-${r.rifleClass}`}
                          onClick={() => navigate(link(`/shooters/${r.sid}`))}
                          className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
                        >
                          <td className="td"><RankChip rank={r.classRank ?? i + 1} /></td>
                          <td className="td">
                            <Link to={link(`/shooters/${r.sid}`)} className="font-bold text-ink hover:text-primary">
                              {r.shooter}
                            </Link>
                            <div className="text-xs text-muted">SID: {r.sid}</div>
                          </td>
                          <td className="td text-slate-600">{r.club}</td>
                          <td className="td text-slate-600">{r.grade || "—"}</td>
                          <td className="td text-center">{r.matches}</td>
                          <td className="td text-right font-bold">
                            {r.score}
                            <span className="font-semibold text-muted">/{r.possible}</span>
                          </td>
                          <td className="td text-right font-semibold">{r.xCount}</td>
                          <td className="td text-right font-bold text-primary">{r.percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {/* The portal-wide board is still a click away — it is just not this. */}
          <p className="text-[13px] text-muted">
            These placings cover {c.name} only.{" "}
            <Link to={link("/rankings")} className="font-bold text-primary hover:underline">
              Portal-wide rankings
            </Link>{" "}
            rank every competitor across all events.
          </p>
        </div>
      )}
    </CompShell>
  );
}

/* --------------------------------- Results ---------------------------------- */

export function CompResults() {
  usePageView("results");
  const link = usePortalLink();
  const { data: events } = useApi<CompetitionEvent[]>("/events", []);
  const { id } = useParams();
  const { data: agg } = useApi<Aggregates>(id ? `/competitions/${id}/aggregates` : null, { daily: [], grand: [] });
  /*
   * Two ways of reading the same results, because they answer different
   * questions. "Results list" is the published record — every trophy and every
   * match, placed by class and grade, the way the sport's result services print
   * it. "Overview" is the winners' view that was already here, and stays.
   */
  const [view, setView] = useState<"list" | "overview">("list");

  // Class Rank, because that is what the rank column now means on this page.
  const exportGrand = (c: Competition) =>
    downloadCsv(
      `${c.id}-grand-aggregate.csv`,
      ["Class", "Class Rank", "Shooter", "SID", "Club", "Grade", "Matches", "Total Score", "Centres"],
      agg.grand.flatMap((g) =>
        g.rows.map((r) => [g.rifleClass, r.rank, r.shooter, r.sid, r.club, r.grade, r.matchesCounted ?? "", r.score, r.xCount])
      )
    );

  return (
    <CompShell active="results" title="Results" subtitle="Results roll up through the competition hierarchy — every level links down to the bullet impacts." icon={BarChart3}>
      {(c) => (
        <>
          <div className="no-print mb-4 inline-flex rounded-xl border border-line bg-white p-1">
            {([
              { key: "list", label: "Results List", icon: ListOrdered },
              { key: "overview", label: "Overview", icon: LayoutGrid },
            ] as const).map((t) => (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-4 text-[13px] font-bold transition-colors ${
                  view === t.key ? "bg-primary text-white" : "text-muted hover:text-ink"
                }`}
              >
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>

          {view === "list" && <ResultsList competitionId={c.id} />}

          {view === "overview" && (
        <>
          {/* The results hierarchy (per the client's model) */}
          <div className="card mb-4 flex flex-wrap items-center gap-1.5 px-4 py-2 text-[13px] font-semibold">
            <span className="rounded-md bg-navy-950 px-2 py-0.5 text-white">Grand Event</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-md bg-primary-soft px-2 py-0.5 text-primary">Events</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-md bg-primary-soft px-2 py-0.5 text-primary">Matches</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-md bg-primary-soft px-2 py-0.5 text-primary">Strings</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-md bg-accent-soft px-2 py-0.5 text-accent">Bullet Impacts</span>
            <span className="ml-2 font-normal text-muted">Aggregates sum upward: strings → match, matches → event, events → grand aggregate.</span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {events
              .filter((e) => e.competitionId === c.id)
              .map((e) => (
                <div key={e.id} className="card p-4 transition-shadow hover:shadow-lift">
                  <div className="flex items-start justify-between">
                    <span className="text-[12px] font-bold uppercase tracking-widest text-primary">Event {e.n}</span>
                    <Badge tone={statusTone(e.status)}>{e.status}</Badge>
                  </div>
                  <h3 className="mt-1.5 text-sm font-bold text-ink">{e.name}</h3>
                  <p className="mt-0.5 text-xs text-muted">{e.date} · {e.distance} · {e.shots} shots · {e.target}</p>
                  <div className="mt-2.5 flex gap-2">
                    <Link
                      to={link(`/competitions/${c.id}/events/${e.id}`)}
                      className="flex h-8 flex-1 items-center justify-center rounded-lg border border-primary text-[13px] font-bold text-primary transition-colors hover:bg-primary-soft"
                    >
                      Event Aggregate
                    </Link>
                    <Link
                      to={link(`/competitions/${c.id}/matches`)}
                      className="flex h-8 flex-1 items-center justify-center rounded-lg border border-line text-[13px] font-bold text-ink transition-colors hover:bg-slate-50"
                    >
                      Matches
                    </Link>
                  </div>
                </div>
              ))}
          </div>

          {/* Grand Aggregate — one row per shooter per class, summed across all matches */}
          <SectionCard
            className="mt-6"
            title={<span className="flex items-center gap-2"><Trophy size={15} className="text-warning" /> Grand Aggregate — all events, ranked within each class</span>}
            action={
              agg.grand.length > 0 ? (
                <button
                  onClick={() => exportGrand(c)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
                >
                  <Download size={13} /> CSV
                </button>
              ) : undefined
            }
          >
            {agg.grand.length ? (
              <AggregateTable classes={agg.grand} />
            ) : (
              <p className="p-4 text-sm text-muted">
                The grand aggregate is generated automatically once shot data has been imported for this competition's
                matches. Classes are ranked separately, and a shooter contesting more than one class appears in each.
              </p>
            )}
          </SectionCard>

          {/* Daily Aggregates */}
          {agg.daily.length > 1 &&
            agg.daily.map((d) => (
              <SectionCard key={d.date} className="mt-6" title={`Daily Aggregate — ${d.date} · ranked within each class`}>
                <AggregateTable classes={d.rows} />
              </SectionCard>
            ))}
        </>
          )}
        </>
      )}
    </CompShell>
  );
}
